import { useEffect, useState } from "react";
import { getMyBACPermissions } from "../service";

/**
 * BAC staff permissions.
 *
 * Usage:
 *   const { can, loading } = useBACPermissions();
 *   {can("scrutiny") && <ScrutinyTab />}
 *
 * Feature keys (must match bac/access.py):
 *   dashboard, case_overview, register_matter, notice, scrutiny, fee_calculator,
 *   financial, arbitrator_tab, daily_proceedings, book_hearing_room, cause_list,
 *   reports, master_tab, dms
 *
 * Users with no assigned role keep their existing access (the backend returns
 * every feature for them), so this is safe to roll out.
 */
export default function useBACPermissions() {
  const [state, setState] = useState({
    permissions: {},
    features: [],
    role: null,
    isFullAccess: false,
    loading: true,
  });

  useEffect(() => {
    let active = true;
    getMyBACPermissions()
      .then((res) => {
        if (!active) return;
        const d = res.data || {};
        setState({
          permissions: d.permissions || {},
          features: d.features || [],
          role: d.role || null,
          isFullAccess: !!d.is_full_access,
          loading: false,
        });
      })
      .catch(() => {
        // On failure show nothing extra rather than over-granting.
        if (!active) return;
        setState({
          permissions: {},
          features: [],
          role: null,
          isFullAccess: false,
          loading: false,
        });
      });
    return () => {
      active = false;
    };
  }, []);

  const { permissions, features, role, isFullAccess, loading } = state;

  // While loading we deny, so a restricted item never flashes on screen.
  const can = (feature) => !loading && !!permissions[feature];

  return { can, permissions, features, role, isFullAccess, loading };
}

export { useBACPermissions };